/**
 * StandardTextarea Component
 *
 * Unified textarea with label, error support, and character count.
 * Consistent styling with dark mode support.
 */

import React from 'react';

export interface StandardTextareaProps {
  label?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  helpText?: string;
  rows?: number;
  maxLength?: number;
  showCharCount?: boolean;
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  required?: boolean;
  className?: string;
}

export const StandardTextarea: React.FC<StandardTextareaProps> = ({
  label,
  value,
  onChange,
  placeholder,
  disabled = false,
  error,
  helpText,
  rows = 4,
  maxLength,
  showCharCount = false,
  resize = 'vertical',
  required = false,
  className = ''
}) => {
  const resizeClass = {
    none: 'resize-none',
    vertical: 'resize-y',
    horizontal: 'resize-x',
    both: 'resize'
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        maxLength={maxLength}
        required={required}
        className={`
          w-full px-4 py-2 rounded-md border
          bg-white dark:bg-gray-800
          text-gray-900 dark:text-gray-100
          placeholder-gray-400 dark:placeholder-gray-500
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
          ${resizeClass[resize]}
          ${error
            ? 'border-red-500 dark:border-red-500 focus:ring-red-500'
            : 'border-gray-300 dark:border-gray-600'
          }
        `}
      />

      <div className="flex items-center justify-between mt-1">
        <div className="flex-1">
          {helpText && !error && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {helpText}
            </p>
          )}

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </div>

        {showCharCount && maxLength && (
          <p className={`text-sm ml-2 ${
            value.length > maxLength * 0.9
              ? 'text-red-600 dark:text-red-400'
              : 'text-gray-500 dark:text-gray-400'
          }`}>
            {value.length} / {maxLength}
          </p>
        )}
      </div>
    </div>
  );
};

export default StandardTextarea;
